/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useEffect, useState } from 'react';
import { beamApi, settingsApi } from '../../api';
import { useAuth } from '../../AuthContext';

import { Empty, ErrorNote, Spinner, useLoader } from '../../components/common';
import { SettingsPage, Panel, Field } from './shell';

const STORE_ID_MAX = 4;

/* branch_business_hour.weekday is 0 = Sunday, so the array index is the key. */
const WEEKDAYS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];

const hhmm = (t) => (t ? String(t).slice(0, 5) : '-');

export function SettingStore() {
  const store = useLoader(() => settingsApi.store(), []);
  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.updateStore(form);
      store.reload();
      setForm(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPage
      tab={tab}
      onTab={setTab}
      tabs={[
        { key: 'info', label: 'ข้อมูลร้าน' },
        { key: 'hours', label: 'เวลา เปิด/ปิด ร้าน' },
        { key: 'branches', label: 'สาขา' },
        { key: 'beam', label: 'เครื่องชำระเงิน Beam' },
      ]}
    >
      {tab === 'branches' ? <BranchTab /> : tab === 'beam' ? <BeamDeviceTab /> : (
      <Panel loader={store}>
        {(d) => {
          if (tab === 'hours') {
            // Read-only: settingsApi has no writer for PUT /admin/store/hours yet,
            // so the tab reports the branch's hours rather than offering a save
            // that would go nowhere.
            return (
              <div className="card store-hours-card">
                <table className="data">
                  <thead>
                    <tr><th>วัน</th><th>เวลาเปิด</th><th>เวลาปิด</th><th>สถานะ</th></tr>
                  </thead>
                  <tbody>
                    {WEEKDAYS.map((day, weekday) => {
                      const h = d.businessHours.find((x) => x.weekday === weekday);
                      const closed = !h || h.is_closed;
                      return (
                        <tr key={day}>
                          <td style={{ fontWeight: 600 }}>{day}</td>
                          <td>{closed ? '-' : hhmm(h.open_time)}</td>
                          <td>{closed ? '-' : hhmm(h.close_time)}</td>
                          <td>
                            <span className={`badge ${closed ? 'badge-red' : 'badge-green'}`}>
                              {closed ? 'ปิด' : 'เปิด'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }

          const values = form || {
            storeId: d.contact.storeId, nameEn: d.branch.name_en, nameTh: d.branch.name_th,
            email: d.contact.email, phone: d.branch.phone,
            facebook: d.contact.facebook, lineId: d.contact.lineId,
            address: d.branch.address,
          };
          const bind = (k) => ({
            value: values[k] || '',
            onChange: (e) => setForm({ ...values, [k]: e.target.value }),
          });

          return (
            <div className="setting-card store-form">
              <Field label="Store ID">
                <span className="input-counter">
                  <input maxLength={STORE_ID_MAX} {...bind('storeId')} />
                  <span className="counter">
                    {(values.storeId || '').length}/{STORE_ID_MAX}
                  </span>
                </span>
              </Field>
              {/* ชื่อร้าน is the organization above the branch, shared by every
                  สาขา — only the branch's own names are editable here. */}
              <Field label="ชื่อร้าน">
                <input value={d.branch.org_name_th || d.branch.org_name_en || ''} disabled />
              </Field>
              <Field label="ชื่อสาขา"><input {...bind('nameEn')} /></Field>
              <Field label="ชื่อสาขา (ไทย)"><input {...bind('nameTh')} /></Field>
              <Field label="อีเมล"><input type="email" {...bind('email')} /></Field>
              <Field label="เบอร์โทรศัพท์"><input {...bind('phone')} /></Field>
              <Field label="Facebook(Link)"><input {...bind('facebook')} /></Field>
              <Field label="LINE ID"><input {...bind('lineId')} /></Field>
              <Field label="ที่อยู่ร้าน"><input {...bind('address')} /></Field>

              <button
                className="btn-solid"
                style={{ marginTop: 16 }}
                disabled={!form || saving}
                onClick={save}
              >
                {saving ? 'กำลังบันทึก…' : 'แก้ไข'}
              </button>
            </div>
          );
        }}
      </Panel>
      )}
    </SettingsPage>
  );
}

function BeamDeviceTab() {
  const { branchId, branches, branch, switchBranch } = useAuth();
  const device = useLoader(() => beamApi.device(true), [branchId]);
  const [pairingCode, setPairingCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A pairing code belongs to the branch selected when it was entered. Never
  // carry an unsent code or an old error across to another branch.
  useEffect(() => {
    setPairingCode('');
    setError(null);
  }, [branchId]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      await beamApi.connectDevice(pairingCode);
      setPairingCode('');
      device.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      `ยืนยันการตัดการเชื่อมต่อเครื่อง Beam ของสาขา ${branch?.code || 'นี้'}?`
    )) return;
    setBusy(true);
    setError(null);
    try {
      await beamApi.disconnectDevice();
      device.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (device.loading) return <Spinner />;
  if (device.error) return <ErrorNote error={device.error} />;

  const status = device.data?.device || {};
  return (
    <div className="setting-card beam-device-card">
      <div className="beam-branch-picker">
        <div>
          <strong>ตั้งค่า Beam แยกตามสาขา</strong>
          <span>เลือกสาขาก่อนกรอก Pairing Code ของเครื่องสาขานั้น</span>
        </div>
        <label>
          <span>สาขา</span>
          <select
            value={branchId || ''}
            onChange={(event) => switchBranch(Number(event.target.value))}
            disabled={busy}
          >
            {branches.map((item) => (
              <option key={item.id} value={item.id}>
                {item.code}{item.nameTh || item.nameEn ? ` — ${item.nameTh || item.nameEn}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="beam-device-head">
        <div>
          <h2>Beam Bolt</h2>
          <p>
            เครื่องรับบัตรเครดิตและแสดง QR PromptPay สำหรับ
            <b> {branch?.code || 'สาขาปัจจุบัน'}</b>
          </p>
        </div>
        <span className={`badge ${status.connected ? 'badge-green' : 'badge-grey'}`}>
          {status.connected ? 'เชื่อมต่อแล้ว' : 'ยังไม่เชื่อมต่อ'}
        </span>
      </div>

      {error && <ErrorNote error={error} />}

      <div className="beam-device-meta">
        <span>Environment</span><strong>{status.environment || '-'}</strong>
        <span>ตั้งค่า API</span><strong>{status.configured ? 'พร้อมใช้งาน' : 'ยังไม่ได้ตั้งค่า'}</strong>
        <span>Device ID</span><strong>{status.deviceId || '-'}</strong>
        <span>สาขาที่เชื่อมต่อ</span><strong>{branch?.code || '-'}</strong>
      </div>

      {!status.configured ? (
        <div className="beam-config-note">
          เพิ่ม BEAM_ENV, BEAM_MERCHANT_ID และ BEAM_API_KEY ใน backend ก่อนเชื่อมต่อเครื่อง
        </div>
      ) : status.connected ? (
        <button className="btn-outline" onClick={disconnect} disabled={busy}>
          {busy ? 'กำลังตัดการเชื่อมต่อ…' : 'ตัดการเชื่อมต่อ'}
        </button>
      ) : (
        <div className="beam-pair-form">
          <label className="field">
            <span>Pairing Code จากหน้าจอ Beam Bolt</span>
            <input
              value={pairingCode}
              onChange={(event) => setPairingCode(event.target.value.trim().toUpperCase())}
              placeholder="เช่น CFC93191"
              disabled={busy}
            />
          </label>
          <p>เปิด Paired Mode บนเครื่อง Beam Bolt แล้วกรอกรหัสที่กำลังแสดงอยู่</p>
          <button
            className="btn-solid"
            onClick={connect}
            disabled={busy || !pairingCode}
          >
            {busy ? 'กำลังเชื่อมต่อ…' : 'เชื่อมต่อเครื่อง Beam'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * สาขา — the shops in this organisation, and how another one gets opened.
 *
 * Their POS has no equivalent: Thaihand opens branches for the shop as an
 * account operation. Running it in-house means the shop does it itself, so this
 * page exists on ours and not on theirs.
 *
 * What a new branch inherits is deliberate — see controllers/admin/branch.js.
 * The team and the price list come across on their own; rooms and beds do not,
 * because those are the one thing that really is particular to an address.
 */
function BranchTab() {
  const { branchId, refresh } = useAuth();
  const list = useLoader(() => settingsApi.branches(), []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: '', nameEn: '', nameTh: '', phone: '', address: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await settingsApi.createBranch(form);
      setForm({ code: '', nameEn: '', nameTh: '', phone: '', address: '' });
      setAdding(false);
      list.reload();
      // The header switcher is built from the session, not from this list.
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (b) => {
    setError(null);
    try {
      if (b.isActive) await settingsApi.closeBranch(b.id);
      else await settingsApi.reopenBranch(b.id);
      list.reload();
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  if (list.loading) return <Spinner />;
  if (list.error) return <ErrorNote message={list.error} onRetry={list.reload} />;

  const branches = list.data?.branches || [];

  return (
    <div className="setting-card">
      {error && <ErrorNote message={error} />}

      {branches.length === 0 ? <Empty icon="branch" message="ยังไม่มีสาขา" /> : (
        <div className="branch-list">
          {branches.map((b) => (
            <div key={b.id} className={`branch-row${b.isActive ? '' : ' closed'}`}>
              <div className="branch-id">
                <span className="branch-code">{b.code}</span>
                {b.id === branchId && <span className="badge badge-purple">สาขาปัจจุบัน</span>}
                {!b.isActive && <span className="badge badge-grey">ปิด</span>}
                {!b.hasAccess && <span className="badge badge-grey">ไม่มีสิทธิ์</span>}
              </div>
              <div className="branch-name">
                {b.nameTh || b.nameEn}
                {b.nameTh && b.nameEn ? <em> ({b.nameEn})</em> : null}
              </div>
              <div className="branch-meta">
                หมอนวด {b.therapistCount} · ห้อง {b.roomCount} · ผู้ใช้ {b.userCount}
              </div>
              <button
                className="btn-ghost"
                disabled={b.id === branchId}
                title={b.id === branchId ? 'สลับไปสาขาอื่นก่อนจึงจะปิดได้' : ''}
                onClick={() => toggle(b)}
              >
                {b.isActive ? 'ปิดสาขา' : 'เปิดอีกครั้ง'}
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="branch-form">
          <Field label="รหัสสาขา">
            <input
              {...bind('code')}
              placeholder="SUKHUMVIT"
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
          </Field>
          <Field label="ชื่อสาขา"><input {...bind('nameEn')} placeholder="Sukhumvit" /></Field>
          <Field label="ชื่อสาขา (ไทย)"><input {...bind('nameTh')} /></Field>
          <Field label="เบอร์โทรศัพท์"><input {...bind('phone')} /></Field>
          <Field label="ที่อยู่ร้าน"><input {...bind('address')} /></Field>

          <p className="branch-note">
            สาขาใหม่จะได้หมอนวดและราคาบริการทั้งหมดทันที · ห้องและเตียงต้องเพิ่มเอง
          </p>

          <div className="branch-actions">
            <button
              className="btn-solid"
              disabled={busy || !form.code || !form.nameEn}
              onClick={submit}
            >
              {busy ? 'กำลังเพิ่ม…' : 'เพิ่มสาขา'}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => setAdding(false)}>
              ยกเลิก
            </button>
          </div>
        </div>
      ) : (
        <button className="btn-solid" style={{ marginTop: 16 }} onClick={() => setAdding(true)}>
          + เพิ่มสาขา
        </button>
      )}
    </div>
  );
}

/* ── บริการ ──────────────────────────────────────────────────────────── */
